import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { ContourAutoSuggestionController } from '~/engine/tool/contourAutoSuggestion/ContourAutoSuggestionController';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import {
    brushOptionsState,
    selectedToolState,
    contourAutoSuggestionEnabledState,
    Tools,
} from '~/state/atoms/toolsAtoms';

/**
 * Tools for which the auto-suggest preview is available.
 *
 * The toggle UI is surfaced in the options tray of each of these
 * tools, so the feature is only meaningful while one of them is the
 * active tool:
 *   - `'brush'` — covers both the static and adaptive brush draw
 *     variants (distinguished by `brushOptionsState`; erase is gated
 *     off below);
 *   - `'lineDeform'` — the line-deform contouring tool.
 *
 * Switching to any other tool (line, deform, pan, box-zoom,
 * transform, registration, poi-move) tears down the controller. The
 * toggle atom itself is preserved across switches so the suggestion
 * comes back automatically when the user returns to an allowed tool.
 *
 * Kept as a `Set` for O(1) lookup so further tools can be added
 * without revisiting the call site.
 */
const AUTO_SUGGEST_ALLOWED_TOOLS: ReadonlySet<Tools> = new Set<Tools>([
    'brush',
    'lineDeform',
]);

/**
 * Drives the {@link ContourAutoSuggestionController} singleton from React
 * state. The controller is enabled iff ALL of the following hold:
 *   - the user toggled the feature on,
 *   - the active tool is one of {@link AUTO_SUGGEST_ALLOWED_TOOLS}
 *     (the brush — static or adaptive — or the line-deform tool),
 *   - if the brush is active, it is in a draw mode — auto-suggest is
 *     suppressed while erase is active because the toggle is hidden
 *     in that options tray and the user is removing content rather
 *     than extending it (this gate does not apply to line-deform,
 *     which has no erase mode),
 *   - the edit target resolves to an Environment: the registry
 *     Environment whose row shows the active structure set,
 *   - the {@link ViewManager} exists,
 *   - a non-point ROI is selected.
 *
 * The controller itself suggests, and commits, only while a view of the
 * Environment displays an image slice, so no gate on the views' geometry
 * is needed here.
 *
 * The controller is handed the Environment, the live set of its views and
 * the signal that one of them changed which image of the fused pair it
 * draws, so a tile that mounts, unmounts or changes sides reaches it
 * through those handles, not through a re-render here. The hook re-runs
 * `disable` then `enable` whenever one of the dependencies above changes so
 * the controller always observes the correct tool + Environment + structure
 * triple.
 */
export function useContourAutoSuggestionController(): void {
    const enabled = useRecoilValue(contourAutoSuggestionEnabledState);
    const tool = useRecoilValue(selectedToolState);
    const brushOptions = useRecoilValue(brushOptionsState);
    const viewManager = useRecoilValue(viewManagerState);
    const { environment, roiNumber } = useActiveEditTarget();

    useEffect(() => {
        const controller = ContourAutoSuggestionController.getInstance();

        // Erase mode only exists for the brush; it must not block
        // auto-suggest under line-deform.
        const eraseBlocks = tool === 'brush' && brushOptions.erase;

        if (
            !enabled ||
            !AUTO_SUGGEST_ALLOWED_TOOLS.has(tool) ||
            eraseBlocks ||
            !environment ||
            !viewManager ||
            roiNumber === -1
        ) {
            controller.disable();
            return;
        }

        const roi = environment.getRoi(roiNumber);
        if (!roi || roi.isPointRoi) {
            controller.disable();
            return;
        }

        controller.enable(
            environment,
            viewManager.viewsFor(environment),
            (listener) =>
                viewManager.subscribeFusedImageDisplay(environment, listener),
            () => environment.getRoi(roiNumber),
            () => viewManager.interactionContext.toolManager.getActiveTool()
        );
        return () => {
            controller.disable();
        };
    }, [
        enabled,
        tool,
        brushOptions.erase,
        environment,
        viewManager,
        roiNumber,
    ]);
}
